import React, { useEffect, useState } from 'react';
import useDragTranslate from '../hooks/useDragTranslate';
import { getRandomWindowPosition } from '../utils/windowPositioning';
import TEXT from '../constants/text';
/* experience folder window component */

const ExperienceWindow = ({ onClose, zIndex, onFocus, onExperienceClick, openDetailWindow }) => {
  const { elRef, onPointerDown, onPointerMove, onPointerUp } = useDragTranslate(onFocus);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  /* drag functionality and selection state */
  
  // Generate random position for this window
  const [windowPosition] = useState(() => getRandomWindowPosition(700, 500));
  
  const handleFocus = () => {
    onFocus && onFocus();
    /* bring window to front when clicked */
  };

  const handleClose = () => {
    // 1) add .closing
    elRef.current?.classList.add('closing');
    /* start the close animation */

    // 2) after animation end, call onClose to unmount
    const done = () => {
      elRef.current?.removeEventListener('animationend', done);
      onClose();
    };
    elRef.current?.addEventListener('animationend', done, { once: true });
    /* wait for animation to finish then actually close */
  };

  // Detect mobile devices and narrow desktop windows
  useEffect(() => {
    const checkMobile = () => {
      const isMobileWidth = window.innerWidth <= 770;
      setIsMobile(isMobileWidth);
      console.log('Window width:', window.innerWidth, 'isMobile:', isMobileWidth);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // listen on the document so we can move outside the window bounds
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    /* set up drag event listeners */
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      /* clean up listeners */
    };
  }, [onPointerMove, onPointerUp]);

  useEffect(() => {
    // clear selection when detail window is closed
    if (!openDetailWindow) {
      setSelectedIndex(null);
    }
  }, [openDetailWindow]);

  // convert constants to array format for display
  const experiences = [
    {
      company: TEXT.EXPERIENCE.INTERDEPENDENT.COMPANY,
      timespan: TEXT.EXPERIENCE.INTERDEPENDENT.TIMESPAN,
      description: TEXT.EXPERIENCE.INTERDEPENDENT.DESCRIPTION,
      role: TEXT.EXPERIENCE.INTERDEPENDENT.ROLE,
      detailData: {
        title: TEXT.EXPERIENCE.INTERDEPENDENT.TITLE,
        subtitle: TEXT.EXPERIENCE.INTERDEPENDENT.SUBTITLE,
        image: TEXT.EXPERIENCE.INTERDEPENDENT.IMAGE,
        sections: TEXT.EXPERIENCE.INTERDEPENDENT.SECTIONS,
        company: TEXT.EXPERIENCE.INTERDEPENDENT.COMPANY
      }
    },
    {
      company: TEXT.EXPERIENCE.QUEST_2_RECOVERY.COMPANY,
      timespan: TEXT.EXPERIENCE.QUEST_2_RECOVERY.TIMESPAN,
      description: TEXT.EXPERIENCE.QUEST_2_RECOVERY.DESCRIPTION,
      role: TEXT.EXPERIENCE.QUEST_2_RECOVERY.ROLE,
      detailData: {
        title: TEXT.EXPERIENCE.QUEST_2_RECOVERY.TITLE,
        subtitle: TEXT.EXPERIENCE.QUEST_2_RECOVERY.SUBTITLE,
        image: TEXT.EXPERIENCE.QUEST_2_RECOVERY.IMAGE,
        sections: TEXT.EXPERIENCE.QUEST_2_RECOVERY.SECTIONS,
        company: TEXT.EXPERIENCE.QUEST_2_RECOVERY.COMPANY
      }
    },
    {
      company: TEXT.EXPERIENCE.VISA.COMPANY,
      timespan: TEXT.EXPERIENCE.VISA.TIMESPAN,
      description: TEXT.EXPERIENCE.VISA.DESCRIPTION,
      role: TEXT.EXPERIENCE.VISA.ROLE,
      detailData: {
        title: TEXT.EXPERIENCE.VISA.TITLE,
        subtitle: TEXT.EXPERIENCE.VISA.SUBTITLE,
        image: TEXT.EXPERIENCE.VISA.IMAGE,
        sections: TEXT.EXPERIENCE.VISA.SECTIONS,
        company: TEXT.EXPERIENCE.VISA.COMPANY
      }
    },
    {
      company: TEXT.EXPERIENCE.IGNITE.COMPANY,
      timespan: TEXT.EXPERIENCE.IGNITE.TIMESPAN,
      description: TEXT.EXPERIENCE.IGNITE.DESCRIPTION,
      role: TEXT.EXPERIENCE.IGNITE.ROLE,
      detailData: {
        title: TEXT.EXPERIENCE.IGNITE.TITLE,
        subtitle: TEXT.EXPERIENCE.IGNITE.SUBTITLE,
        image: TEXT.EXPERIENCE.IGNITE.IMAGE,
        sections: TEXT.EXPERIENCE.IGNITE.SECTIONS,
        company: TEXT.EXPERIENCE.IGNITE.COMPANY
      }
    },
    {
      company: TEXT.EXPERIENCE.UC_RIVERSIDE.COMPANY,
      timespan: TEXT.EXPERIENCE.UC_RIVERSIDE.TIMESPAN,
      description: TEXT.EXPERIENCE.UC_RIVERSIDE.DESCRIPTION,
      role: TEXT.EXPERIENCE.UC_RIVERSIDE.ROLE,
      detailData: {
        title: TEXT.EXPERIENCE.UC_RIVERSIDE.TITLE,
        subtitle: TEXT.EXPERIENCE.UC_RIVERSIDE.SUBTITLE,
        image: TEXT.EXPERIENCE.UC_RIVERSIDE.IMAGE,
        sections: TEXT.EXPERIENCE.UC_RIVERSIDE.SECTIONS,
        company: TEXT.EXPERIENCE.UC_RIVERSIDE.COMPANY
      }
    }
  ];
  /* experience list with new data */

  return (
    <div
      ref={elRef}
      className="window"
      data-window="experience"
      style={isMobile ? {
        display: 'block',
        width: '100vw',
        height: 'calc(100vh - 35px)',
        zIndex: zIndex,
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '35px',
        transform: 'translate(0, 0)',
        // Mobile: full screen layout
      } : {
        display: 'block',
        width: '800px',
        height: '500px',
        zIndex: zIndex,
        position: 'fixed',
        '--tx': `${windowPosition.tx}px`,
        '--ty': `${windowPosition.ty}px`,
        // Desktop: draggable window
      }}
      onClick={handleFocus}
    >
      <div className="title-bar draggable" onPointerDown={onPointerDown}>
        <div className="title-bar-text">Experience - Windows Explorer</div>
        <div className="title-bar-controls">
          <button aria-label="Close" onClick={handleClose}></button>
        </div>
      </div>
      {/* window title bar with close button */}
      
      <div className="window-body">
        {isMobile ? (
          <div className="experience-cards-container">
            {/* Mobile card layout */}
            {experiences.map((experience, index) => (
              <div 
                key={index} 
                className={`experience-card ${selectedIndex === index ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedIndex(index);
                  onExperienceClick && onExperienceClick(experience.detailData);
                }}
              >
                <div className="card-header">
                  <div className="card-company">
                    <img src="/assets/filepixel.png" alt="File" className="card-icon" />
                    {experience.company}
                  </div>
                </div>
                <div className="card-timespan">{experience.timespan}</div>
                <div className="card-content">
                  <div className="card-role">{experience.role}</div>
                  <div className="card-description">{experience.description}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="explorer-list experience-list">
            {/* Desktop list layout */}
            <div className="list-header">
              <div className="header-company">Company</div>
              <div className="header-timespan">Timespan</div>
              <div className="header-description">Description</div>
              <div className="header-role">Role</div>
            </div>
            {/* explorer list header */}
            
            {experiences.map((experience, index) => (
              <div 
                key={index} 
                className={`list-item ${selectedIndex === index ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedIndex(index);
                  onExperienceClick && onExperienceClick(experience.detailData);
                }}
              >
                <div className="item-company">
                  <img src="/assets/filepixel.png" alt="File" className="item-icon" />
                  {experience.company}
                </div>
                <div className="item-timespan">{experience.timespan}</div>
                <div className="item-description">{experience.description}</div>
                <div className="item-role">{experience.role}</div>
              </div>
            ))}
            {/* explorer list items */}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExperienceWindow;
